// © 2026 Camp Promethean, LLC. All Rights Reserved.
// Single source of truth for plan-change surfaces (success page, downgrade
// interstitials, banners). Spec: docs/upgrade-downgrade-flow-spec.md

import type { MembershipTier } from "@/types/navigation";

export type PaidTier = Exclude<MembershipTier, "free">;
export type PlanChangeKind = "new" | "upgrade" | "downgrade";

export interface TierHighlight {
  title: string;
  description: string;
  path: string;
}

export interface PlanChangeTierContent {
  /** One-line promise shown under "You're on <tier>". */
  tagline: string;
  /** Exactly three unlock highlights, hero first. */
  highlights: TierHighlight[];
  primaryCta: { label: string; path: string };
  /**
   * Sharp names of what this tier uniquely provides — shown as the loss list
   * when a user downgrades below it.
   */
  lossList: string[];
}

export const TIER_ORDER: MembershipTier[] = [
  "free",
  "Basic",
  "Advanced",
  "Professional",
];

export function isPaidTier(value: string | null | undefined): value is PaidTier {
  return value === "Basic" || value === "Advanced" || value === "Professional";
}

export function tierRank(tier: string | null | undefined): number {
  return TIER_ORDER.indexOf((tier as MembershipTier) ?? "free");
}

/** true when `have` satisfies `need` (equal or higher tier). */
export function tierSatisfies(
  have: string | null | undefined,
  need: string | null | undefined,
): boolean {
  return tierRank(have) >= tierRank(need);
}

export function planChangeKind(
  from: string | null | undefined,
  to: string,
): PlanChangeKind {
  const fromRank = tierRank(from);
  if (fromRank <= 0) return "new";
  return tierRank(to) > fromRank ? "upgrade" : "downgrade";
}

export const PLAN_CHANGE_CONTENT: Record<PaidTier, PlanChangeTierContent> = {
  Basic: {
    tagline: "Your daily cultivation toolkit is unlocked.",
    highlights: [
      {
        title: "Arca cultivation logs",
        description: "Runoff, agrochem, and IPM logging built for daily ops.",
        path: "/arca/runofflogger",
      },
      {
        title: "Ancora courses & certifications",
        description: "Grower academy with interactive courses and assessments.",
        path: "/ancora/courses",
      },
      {
        title: "Grower toolbox & calculators",
        description: "Precise calculators and trackers for everyday tasks.",
        path: "/arca",
      },
    ],
    primaryCta: { label: "Log your first runoff", path: "/arca/runofflogger" },
    lossList: [
      "Arca cultivation logs",
      "Ancora courses & certifications",
      "Grower toolbox & calculators",
    ],
  },
  Advanced: {
    tagline: "Genetics, yield economics, and the marketplace are yours.",
    highlights: [
      {
        title: "Animus strain library & pheno hunts",
        description: "Build your genetics library and run structured pheno hunts.",
        path: "/animus/library",
      },
      {
        title: "Abacus yield analytics",
        description: "Tie dollars to batches and keep quality on the record.",
        path: "/abacus/logger",
      },
      {
        title: "Agora marketplace",
        description: "Vetted products, professionals, and consultants on demand.",
        path: "/agora/products",
      },
    ],
    primaryCta: { label: "Open your strain library", path: "/animus/library" },
    lossList: [
      "Animus strain library & pheno hunts",
      "Abacus yield analytics",
      "Agora marketplace",
      "Advanced courses",
    ],
  },
  Professional: {
    tagline: "Your whole facility, managed and intelligent.",
    highlights: [
      {
        title: "Prometheus AI co-pilot",
        description: "Ask your facility anything — charts and sources included.",
        path: "/prometheus",
      },
      {
        title: "Atlas facility ops",
        description: "Resources, production calendar, and SOPs in one ERP.",
        path: "/atlas/resources",
      },
      {
        title: "Aurum compliance vault",
        description: "Logs engine, SDS inventory, and purchase archive.",
        path: "/aurum/logs",
      },
    ],
    primaryCta: { label: "Ask Prometheus anything", path: "/prometheus" },
    lossList: [
      "Prometheus AI co-pilot",
      "Atlas facility ops (resources, calendar, SOPs)",
      "Aurum compliance vault",
      "Priority support",
    ],
  },
};

/**
 * Everything lost when moving from `from` down to `to` — the loss lists of
 * every tier above the destination, highest first.
 */
export function lossListForDowngrade(
  from: string | null | undefined,
  to: string | null | undefined,
): string[] {
  const fromRank = tierRank(from);
  const toRank = tierRank(to);
  const lost: string[] = [];
  for (let rank = fromRank; rank > toRank && rank > 0; rank -= 1) {
    const tier = TIER_ORDER[rank];
    if (isPaidTier(tier)) lost.push(...PLAN_CHANGE_CONTENT[tier].lossList);
  }
  return lost;
}
